<template>
	<ModalHeader
		@click-button="clickModalCta"
		:button-disabled="!selectedQuantity"
		class="modal-header"
		:class="{active}"
		ref="header"
		:title="bottomSheetTitle"
		:button-text="modalButtonText"
	/>
	<div v-show="active" class="row">
		<AppCard class="card" inset no-header>
			<template v-slot:body>
				<AppList class="list" ref="list" :items="multiSelectActions">
					<template v-slot:button="{item}">
						<div class="row centered space-between p-10">
							<p class="list-item-title">{{ item.title }}</p>
							<AppButton @click="item.callback()" small outline :text="item.title" />
						</div>
					</template>
					<template v-slot:dropdown-header="{item}">
						<div @click="clickListDropdownHeader(item)" class="dropdown-header row">
							<p class="list-item-title">{{ item.title }}</p>
							<AppChevronIcon :direction="item.active ? 'up' : 'down'" />
						</div>
					</template>
					<template v-slot:dropdown-content-discount="{item}">
						<div class="row">
							<div class="col-6 discount-input-col align-end">
								<AppRadioButtonGroup
									:values="[getString('percent'), getString('exact_amount', [currencyStore.currCurrency.symbol])]"
									@radio-chosen="switchTypeOfDiscount"
								/>
							</div>
							<div class="col-6 discount-input-col flex-end">
								<div class="row align-end flex-end">
									<AppNumberInput :double-decimal="!isPercentDiscount" :right-side-symbol="isPercentDiscount" :symbol="isPercentDiscount ? '%' : null" :value="discount" @changed-value="(v) => discount = v" :min="0" :max="isPercentDiscount ? 100 : null" class="mr-20" />
									<AppButton @click="clickApplyDiscount(false)" small outline :text="getString('apply')" />
								</div>
							</div>
						</div>
					</template>
					<template v-slot:dropdown-content-availability="{item}">
						<div class="row align-end flex-end">
							<AppSwitch @switched="v=>{availability=v}" :checked="availability" :label="getString('available')" class="mr-20" />
							<AppButton @click="clickApplyAvailability" small outline :text="getString('apply')" />
						</div>
					</template>
					<template v-slot:dropdown-content-price="{item}">
						<div class="row">
							<div class="col-6 discount-input-col centered align-end">
								<p>{{getString('price_symbol', [currencyStore.currCurrency.symbol])}}</p>
							</div>
							<div class="col-6 discount-input-col flex-end">
								<div class="row align-end flex-end">
									<AppNumberInput double-decimal :min="0" :value="price" @changed-value="(v) => price = v" class="mr-20" />
									<AppButton @click="clickApplyPrice(false)" small outline :text="getString('apply')" />
								</div>
							</div>
						</div>
					</template>
					<template v-slot:dropdown-content-category="{item}">
						<div class="row align-end flex-end">
							<AppSelect
								@selected="switchSelectedMoveCategory"
								ref="move-category-select"
								:values="sortedCategoriesNames" :label="getString('target_category')"
							/>
							<AppButton class="ml-20" @click="clickMove" small outline :text="getString('apply')" />
						</div>
					</template>
					<template v-slot:dropdown-content-duplicate="{item}">
						<div v-show="selectedQuantity <= 100" class="row align-end flex-end">
							<div class="col-12">
								<div class="row align-end flex-end">
									<AppSelect
										@selected="switchSelectedTargetCategory"
										ref="duplicate-category-select"
										:values="sortedCategoriesNamesWithSame" :label="getString('target_category')"
									/>
									<AppButton class="ml-20" @click="clickDuplicate(false)" small :text="getString('apply')" />
								</div>
							</div>
							<div v-if="unsavedCategories" class="col-12 centered align-end mt-10 opacity-60">
								<p><small>{{getString('unsaved_categories_copy')}}</small></p>
							</div>
						</div>
						<div v-show="selectedQuantity > 100" class="row centered p-20">
							<p>{{getString('too_many_duplicate')}}</p>
						</div>
					</template>
				</AppList>
			</template>
		</AppCard>
		<AppPopup
			:title="getString('price_mismatch_title')"
			:body-text="getString('price_mismatch_message')"
			:buttons="popupPriceMismatchButtons"
			ref="price-mismatch-popup"
		/>
		<AppPopup
			:title="getString('discount_mismatch_title')"
			:body-text="getString('discount_mismatch_message', [currencyStore.readableCurrencyValue(discount, false)])"
			:buttons="popupDiscountMismatchButtons"
			ref="discount-mismatch-popup"
		/>
		<AppPopup
			:title="getString('action_saves_menu_title', [userStore.menuWord])"
			:body-text="getString('action_saves_menu_message')"
			:buttons="popupSaveMenuButtons"
			ref="save-menu-popup"
		/>
	</div>
</template>

<script>
import AppList from "@/shared/components/AppList.vue";
import AppCard from "@/shared/components/AppCard.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AppSwitch from "@/shared/components/AppSwitch.vue";
import AppPopup from "@/shared/components/AppPopup.vue";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppRadioButtonGroup from "@/shared/components/AppRadioButtonGroup.vue";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useCurrencyStore, useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useMenuStore, useUserStore} from "@/console-new/stores";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let menuStore = useMenuStore();
		let notificationStore = useNotificationStore();
		let userStore = useUserStore();
		const currencyStore = useCurrencyStore();
		return {getString: languageStore.getString, showNotification: notificationStore.showNotification, menuStore, currencyStore, userStore};
	},
	name: "MultiSelectEditor",
	components: {AppButton, ModalHeader, AppList, AppCard, AppNumberInput, AppSwitch, AppSelect, AppPopup, AppRadioButtonGroup, AppChevronIcon},
	emits: ["click-modal-cta"],
	props: {
		active: {
			type: Boolean,
			default: false
		},
	},
	data() {
		return {
			multiSelectActions: [
				{
					title: this.getString('move_to_category'),
					customSlot: 'dropdown-header',
					dropdownSlot: 'dropdown-content-category',
				},
				{
					title: this.getString('duplicate'),
					customSlot: 'dropdown-header',
					dropdownSlot: 'dropdown-content-duplicate',
				},
				{
					title: this.getString('discount'),
					customSlot: 'dropdown-header',
					dropdownSlot: 'dropdown-content-discount',
				},
				{
					title: this.getString('price'),
					customSlot: 'dropdown-header',
					dropdownSlot: 'dropdown-content-price',
				},
				{
					title: this.getString('availability'),
					customSlot: 'dropdown-header',
					dropdownSlot: 'dropdown-content-availability',
				},
				{
					title: this.getString('delete'),
					customSlot: 'button',
					callback: this.clickDelete,
				},
			],
			popupDiscountMismatchButtons: [
				{text: this.getString('cancel'), callback: null, negative: true},
				{text: this.getString('ok'), callback: () => {this.clickApplyDiscount(true)}},
			],
			popupPriceMismatchButtons: [
				{text: this.getString('cancel'), callback: null, negative: true},
				{text: this.getString('ok'), callback: () => {this.clickApplyPrice(true)}},
			],
			popupSaveMenuButtons: [
				{text: this.getString('cancel'), callback: null, negative: true},
				{text: this.getString('ok'), callback: () => {this.clickDuplicate(true)}},
			],
			duplicateTargetCategory: null,
			moveTargetCategory: null,
			duplicateTargetCategoryTempId: null,
			moveTargetCategoryTempId: null,
			typeOfDiscount: 0,
			availability: false,
			price: 0,
			discount: 0,
		}
	},
	computed: {
		selectedQuantity() {
			return this.menuStore.selectedItems().length;
		},
		isPercentDiscount() {
			return this.typeOfDiscount === 0;
		},
		bottomSheetTitle() {
			let stringKey = this.selectedQuantity === 1 ? "1_product_selected" : "x_selected";
			return this.getString(stringKey, [this.selectedQuantity]);
		},
		modalButtonText() {
			return this.getString(this.active ? 'select_others' : 'edit');
		},
		sortedCategories() {
			return Object.values(this.menuStore.modMenu.categories).sort((a, b) => a.order_number - b.order_number);
		},
		sortedCategoriesNames() {
			return this.sortedCategories.map(cat => cat.name);
		},
		sortedCategoriesNamesWithSame() {
			let sortedCategoriesNamesNoNew = this.sortedCategories.filter(cat => !!cat.menu_category_id).map(cat => cat.name);
			sortedCategoriesNamesNoNew.push(this.getString('same_category'));
			return sortedCategoriesNamesNoNew;
		},
		unsavedCategories() {
			return !!this.sortedCategories.filter(cat => !cat.menu_category_id).length;
		},
	},
	methods: {
		closeAllDropdowns() {
			this.multiSelectActions.forEach(action => {
				action.active = false;
			});
		},
		resetData() {
			this.resetDuplicateTargetCategory();
			this.resetMoveToCategory();
			this.availability = false;
			this.price = 0;
			this.discount = 0;
			this.closeAllDropdowns();
		},
		resetDuplicateTargetCategory() {
			this.duplicateTargetCategory = this.sortedCategoriesNamesWithSame.length - 1;
			this.$refs['duplicate-category-select'].currentIndex = this.duplicateTargetCategory;
			this.duplicateTargetCategoryTempId = null;
		},
		resetMoveToCategory() {
			if(!this.sortedCategoriesNames || !this.sortedCategoriesNames.length) {
				this.moveTargetCategory = null;
			} else {
				this.moveTargetCategory = 0;
			}
			this.$refs['move-category-select'].currentIndex = this.moveTargetCategory;
			this.moveTargetCategoryTempId = this.moveTargetCategory !== null ? this.sortedCategories[this.moveTargetCategory].tempid : null;
		},
		clickModalCta(openCategoryTempId = null) {
			this.$emit("click-modal-cta", openCategoryTempId);
			this.resetData();
		},
		clickListDropdownHeader(targetAction) {
			this.multiSelectActions.forEach(action => {
				if(action.active && targetAction !== action) {
					action.active = false;
				}
			});
			targetAction.active = !targetAction.active;
		},
		clickDelete() {
			this.menuStore.selectedItems().forEach(item => {

				if(item.menu_category_id) {
					let targetCategoryTempId = this.menuStore.categories[item.menu_category_id].tempid;
					delete this.menuStore.modMenu.categories[targetCategoryTempId].items[item.tempid];
				} else {
					delete this.menuStore.modMenu.orphanItems[item.tempid];
				}

			});



		},
		clickDuplicate(forced = false) {
			if(!forced) {
				this.$refs['save-menu-popup'].toggleStatus(true);
				return;
			}
			if(this.selectedQuantity <= 100) {
				this.$refs['save-menu-popup'].toggleStatus(false);
				let targetCategory = '';
				let singleTargetCategory = true;
				let itemsIdsToDuplicate = [];

				this.menuStore.selectedItems().forEach(item => {
					if(targetCategory) {
						if(targetCategory !== item.menu_category_id && ((!!item.menu_category_id && targetCategory === 'orphan') || (!item.menu_category_id && targetCategory !== 'orphan'))) {
							singleTargetCategory = false;
						}
					}
					targetCategory = item.menu_category_id || 'orphan';
					itemsIdsToDuplicate.push(item.tempid);
				});

				this.menuStore.duplicateSelectedItems(this.duplicateTargetCategoryTempId, (success, errorDetails) =>{
					if(success) {
						let finalCategoryToOpen = this.duplicateTargetCategoryTempId;
						if(!finalCategoryToOpen) {
							finalCategoryToOpen = singleTargetCategory && targetCategory ? targetCategory : null;
						}
						let notificationKey = this.selectedQuantity === 1 ? 'duplicated-product-success' : 'duplicated-products-success';
						this.showNotification(notificationKey);
						this.clickModalCta(finalCategoryToOpen);
					} else if(errorDetails === 'auth_denied') {
						this.showNotification("auth-denied-error");
					} else if(errorDetails === 'too_many_products') {
						this.showNotification("too-many-duplicate");
					} else {
						this.showNotification("generic-fail");
					}
				});
			} else {
				this.showNotification('too-many-duplicate');
			}
		},
		clickMove() {
			if(this.moveTargetCategoryTempId) {
				let deletingItems = [];
				let insertingItems = [];
				this.menuStore.selectedItems().forEach(item => {

					let targetCatId = this.menuStore.modMenu.categories[this.moveTargetCategoryTempId].menu_category_id;
					if(this.moveTargetCategoryTempId && targetCatId !== item.menu_category_id) {
						let originCatTempId = item.menu_category_id ? this.menuStore.categories[item.menu_category_id].tempid : null;
						deletingItems.push({
							tempCatId: originCatTempId,
							tempItemId: item.tempid
						});
						insertingItems.push(Object.assign({}, item, {menu_category_id: targetCatId}));
					}

				});

				deletingItems.forEach(item => {
					if(item.tempCatId) {
						delete this.menuStore.modMenu.categories[item.tempCatId].items[item.tempItemId];
					} else {
						delete this.menuStore.modMenu.orphanItems[item.tempItemId];
					}
				});
				insertingItems.forEach(item => {
					this.menuStore.modMenu.categories[this.moveTargetCategoryTempId].items[item.tempid] = item;
				});

				let notificationKey = this.selectedQuantity === 1 ? 'moved-product-success' : 'moved-products-success';
				this.showNotification(notificationKey);
				this.clickModalCta(this.moveTargetCategoryTempId);
			} else {
				this.showNotification('no-target-category-selected');
			}
		},
		clickApplyAvailability() {
			let targetCategory = '';
			let singleTargetCategory = true;
			this.menuStore.selectedItems().forEach(item => {
				if(targetCategory) {
					if(targetCategory !== item.menu_category_id && ((!!item.menu_category_id && targetCategory === 'orphan') || (!item.menu_category_id && targetCategory !== 'orphan'))) {
						singleTargetCategory = false;
					}
				}
				targetCategory = item.menu_category_id || 'orphan';
				item.available = this.availability;
			});
			this.showNotification('availability-changed');
			let finalCategoryToOpen = singleTargetCategory && targetCategory ? targetCategory : null;
			this.clickModalCta(finalCategoryToOpen);
		},
		clickApplyPrice(forced) {
			if(this.canApplyPrice() || forced) {
				this.$refs['price-mismatch-popup'].toggleStatus(false);
				let targetPrice = this.currencyStore.actualToDbValue(this.price);
				let targetCategory = '';
				let singleTargetCategory = true;

				this.menuStore.selectedItems().forEach(item => {
					if(targetCategory) {
						if(targetCategory !== item.menu_category_id && ((!!item.menu_category_id && targetCategory === 'orphan') || (!item.menu_category_id && targetCategory !== 'orphan'))) {
							singleTargetCategory = false;
						}
					}
					targetCategory = item.menu_category_id || 'orphan';

					if(item.full_price < targetPrice) {
						item.full_price = null;
					}
					item.price = targetPrice;
				});

				this.showNotification('price-changed');
				let finalCategoryToOpen = singleTargetCategory && targetCategory ? targetCategory : null;
				this.clickModalCta(finalCategoryToOpen);
			} else {
				this.promptPriceMismatch();
			}
		},
		clickApplyDiscount(forced) {
			if(this.canApplyDiscount() || forced) {
				this.$refs['discount-mismatch-popup'].toggleStatus(false);
				let targetCategory = '';
				let singleTargetCategory = true;

				this.menuStore.selectedItems().forEach(item => {
					if(targetCategory) {
						if(targetCategory !== item.menu_category_id && ((!!item.menu_category_id && targetCategory === 'orphan') || (!item.menu_category_id && targetCategory !== 'orphan'))) {
							singleTargetCategory = false;
						}
					}
					targetCategory = item.menu_category_id || 'orphan';

					item.full_price = item.full_price || item.price || 0;
					if(this.discount === 0) {
						item.price = item.full_price;
						item.full_price = 0;
					} else if(this.isPercentDiscount) {
						//percent
						item.price = item.full_price * (1 - this.discount/100);
					} else {
						//absolute
						let targetPrice = item.full_price - this.currencyStore.actualToDbValue(this.discount);
						targetPrice = targetPrice < 0 ? 0 : targetPrice;
						item.price = targetPrice;
					}
				});

				this.showNotification('discount-applied');
				let finalCategoryToOpen = singleTargetCategory && targetCategory ? targetCategory : null;
				this.clickModalCta(finalCategoryToOpen);
			} else {
				this.promptDiscountMismatch();
			}
		},
		switchSelectedTargetCategory(index) {
			if(index === this.sortedCategoriesNamesWithSame.length - 1) {
				this.duplicateTargetCategoryTempId = null;
			} else {
				this.duplicateTargetCategoryTempId = this.sortedCategories[index].tempid;
			}
		},
		switchSelectedMoveCategory(index) {
			this.moveTargetCategoryTempId = this.sortedCategories[index].tempid;
		},
		switchTypeOfDiscount(type) {
			this.typeOfDiscount = type;
		},
		canApplyPrice() {
			let targetPrice = this.currencyStore.actualToDbValue(this.price);
			let returning = true;
			this.menuStore.selectedItems().forEach(item => {
				if(item.full_price && item.full_price < targetPrice) {
					returning = false;
				}
			});
			return returning;
		},
		canApplyDiscount() {
			if(this.isPercentDiscount) { return true }
			let returning = true;
			this.menuStore.selectedItems().forEach(item => {
				let targetPrice = item.full_price || item.price || 0;
				if(targetPrice < this.currencyStore.actualToDbValue(this.discount)) {
					returning = false;
				}
			});
			return returning;
		},
		promptPriceMismatch() {
			this.$refs['price-mismatch-popup'].toggleStatus();
		},
		promptDiscountMismatch() {
			this.$refs['discount-mismatch-popup'].toggleStatus();
		},
	},
}
</script>

<style lang="scss" scoped>
.modal-header:not(.active) {
	background-color: unset;
	box-shadow: unset;
	margin-bottom: 0;
}

.card:deep(.card-body) {
	padding: 0 12px;
}
.list {
	:deep(.list-item) {
		padding: 8px 0;
	}
	:deep(.list-item .list-item-header) {
		padding: 10px 0;
	}
	:deep(.list-item:first-of-type:before),
	:deep(.list-item:last-of-type:after) {
		display: none;
	}
	:deep(.dropdown-header) {
		@include centered;
		@include noSelect;
		justify-content: space-between;
		cursor: pointer;
		padding: 10px 0;
	}
	:deep(.discount-input-col) {
		@media (max-width: 600px) {
			flex: 0 0 100%;
			max-width: 100%;

			&:nth-of-type(1) {
				margin-bottom: 20px;
			}
		}
	}
	:deep(.select-wrapper) {
		@media (max-width: 500px) {
			width: 100%;
			max-width: 100%;

			&:nth-of-type(1) {
				margin-bottom: 20px;
			}
		}
	}
}
</style>